import { Minus, Plus } from "lucide-react";
import { cn } from "../../lib/cn";
import { useDesign } from "../../lib/store";

interface Props {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
  className?: string;
  label?: string;
  /**
   * `design` (default) records plus/minus as one room undo step and types
   * into one gesture. `off` leaves history to the caller (import preview,
   * furniture creator).
   */
  history?: "design" | "off";
  /** `id` for the input, so another control can focus it. */
  inputId?: string;
}

export function NumberStepper({
  value,
  onChange,
  step = 1,
  min,
  max,
  suffix,
  className,
  label,
  history = "design",
  inputId,
}: Props) {
  function bump(delta: number) {
    let next = value + delta;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    if (history === "off") onChange(next);
    else useDesign.getState().coalesceEdit(() => onChange(next));
  }
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label && <span className="text-xs font-medium text-ink-500">{label}</span>}
      <div className="inline-flex items-center rounded-xl bg-white ring-1 ring-ink-200 overflow-hidden">
        <button
          type="button"
          onClick={() => bump(-step)}
          className="h-10 w-10 inline-flex items-center justify-center text-ink-500 hover:bg-ink-50 active:bg-ink-100 transition-colors"
          aria-label="Decrease"
        >
          <Minus className="size-4" />
        </button>
        <input
          id={inputId}
          type="number"
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) onChange(v);
          }}
          onFocus={() => {
            if (history !== "off") useDesign.getState().beginGesture();
          }}
          onBlur={() => {
            if (history !== "off") useDesign.getState().endGesture();
          }}
          className="h-10 w-16 text-center text-sm font-medium tabular-nums bg-transparent outline-none"
        />
        {suffix && (
          <span className="pr-2 text-xs text-ink-400 select-none">{suffix}</span>
        )}
        <button
          type="button"
          onClick={() => bump(step)}
          className="h-10 w-10 inline-flex items-center justify-center text-ink-500 hover:bg-ink-50 active:bg-ink-100 transition-colors"
          aria-label="Increase"
        >
          <Plus className="size-4" />
        </button>
      </div>
    </div>
  );
}
